import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cccdAdmin, type AdminTeamDetail, type CatalogOption } from '@/lib/api/cccdAdminClient';
import { btnSave, btnSecondary, StyledSelect, inputCls, labelCls, Toggle } from '@/components/admin/FormComponents';
import { COUNTRIES, getStatesForCountry } from '@alascorporation/utils';
import { SectionHeader } from '@/components/cccd/SectionHeader';
import Alert from '@/components/ui/alert/Alert';
import { toastError } from '@/lib/toast';

// Campos catálogo-driven de la ficha (ver openspec/changes/cccd-institution-tracking).
// El value del select es el `value` guardado en CccdTeam; las opciones se
// cargan desde /cccd/admin/catalog-options?fieldKey=<key>. `motor` es la
// excepción — no usa el catálogo global sino los motores habilitados en la
// categoría del equipo (cccd-ux-redesign, cccd-admin-design-system).
const CATALOG_FIELDS: { field: keyof FichaForm; fieldKey: string; label: string }[] = [
  { field: 'formsStatus', fieldKey: 'forms', label: 'Forms' },
  { field: 'fotosRrss', fieldKey: 'fotos_rrss', label: 'Fotos RRSS' },
  { field: 'kitStatus', fieldKey: 'kit_status', label: 'Kit (entrega)' },
  { field: 'altimetroStatus', fieldKey: 'altimetro_status', label: 'Altímetro (entrega)' },
  { field: 'inscripcionStatus', fieldKey: 'inscripcion_status', label: 'Inscripción' },
  { field: 'estadoOperativo', fieldKey: 'estado_operativo', label: 'Estado operativo' },
];

type FichaForm = {
  motor: string;
  formsStatus: string;
  fotosRrss: string;
  kitStatus: string;
  altimetroStatus: string;
  inscripcionStatus: string;
  estadoOperativo: string;
  publicacion: boolean;
  circular: boolean;
  plataforma: boolean;
  pais: string;
  municipioDepartamento: string;
};

function toForm(team: AdminTeamDetail): FichaForm {
  return {
    motor: team.motor ?? '',
    formsStatus: team.formsStatus ?? '',
    fotosRrss: team.fotosRrss ?? '',
    kitStatus: team.kitStatus ?? '',
    altimetroStatus: team.altimetroStatus ?? '',
    inscripcionStatus: team.inscripcionStatus ?? '',
    estadoOperativo: team.estadoOperativo ?? '',
    publicacion: team.publicacion ?? false,
    circular: team.circular ?? false,
    plataforma: team.plataforma ?? false,
    pais: team.pais ?? '',
    municipioDepartamento: team.municipioDepartamento ?? '',
  };
}

export function TeamFichaForm({ team, onSaved }: { team: AdminTeamDetail; onSaved: () => void }) {
  const [optionsByField, setOptionsByField] = useState<Record<string, CatalogOption[]>>({});
  const [form, setForm] = useState<FichaForm>(toForm(team));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all(CATALOG_FIELDS.map((f) => cccdAdmin.listCatalogOptions(f.fieldKey)))
      .then((results) => {
        const map: Record<string, CatalogOption[]> = {};
        CATALOG_FIELDS.forEach((f, i) => { map[f.fieldKey] = results[i]; });
        setOptionsByField(map);
      })
      .catch(() => setOptionsByField({}));
  }, []);

  useEffect(() => setForm(toForm(team)), [team]);

  const { data: categories = [] } = useQuery({
    queryKey: ['event-categories', team.scope, team.event?.id ?? team.regionalEvent?.id],
    queryFn: () =>
      team.event
        ? cccdAdmin.getEventCategories(team.event.id)
        : cccdAdmin.getRegionalCategories(team.regionalEvent!.id),
    enabled: Boolean(team.event ?? team.regionalEvent),
  });
  const category = categories.find((c) => c.slug === team.categoryId);
  const availableMotors = (category?.availableMotors ?? '')
    .split(',')
    .map((m) => m.trim())
    .filter(Boolean);

  const set = <K extends keyof FichaForm>(key: K, value: FichaForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // pais/municipioDepartamento persisten como nombre (no ISO code), mismo
  // criterio que StudentProfileSections.tsx — el isoCode del país solo
  // maneja el cascade, nunca se guarda.
  const countryCode = COUNTRIES.find((c) => c.label === form.pais)?.value ?? '';
  const departments = getStatesForCountry(countryCode);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await cccdAdmin.updateTeam(team.id, {
        motor: form.motor || null,
        formsStatus: form.formsStatus || null,
        fotosRrss: form.fotosRrss || null,
        kitStatus: form.kitStatus || null,
        altimetroStatus: form.altimetroStatus || null,
        inscripcionStatus: form.inscripcionStatus || null,
        estadoOperativo: form.estadoOperativo || null,
        publicacion: form.publicacion,
        circular: form.circular,
        plataforma: form.plataforma,
        pais: form.pais || null,
        municipioDepartamento: form.municipioDepartamento || null,
      });
      onSaved();
    } catch (err: any) {
      const message = err?.response?.data?.error ?? 'Error al guardar la ficha.';
      setError(message);
      toastError(message);
    } finally {
      setSaving(false);
    }
  };

  const equipmentFields = CATALOG_FIELDS.filter((f) => f.field === 'kitStatus' || f.field === 'altimetroStatus');
  const docFields = CATALOG_FIELDS.filter((f) => f.field !== 'kitStatus' && f.field !== 'altimetroStatus');

  function renderCatalogSelect(field: keyof FichaForm, fieldKey: string, label: string) {
    return (
      <div key={field}>
        <label className={labelCls}>{label}</label>
        <StyledSelect value={form[field] as string} onChange={(e) => set(field, e.target.value as never)}>
          <option value="">Sin definir</option>
          {(optionsByField[fieldKey] ?? []).map((opt) => (
            <option key={opt.id} value={opt.value}>{opt.label}</option>
          ))}
          {/* Preserva un valor histórico aunque ya no esté activo en el catálogo */}
          {form[field] && !(optionsByField[fieldKey] ?? []).some((o) => o.value === form[field]) && (
            <option value={form[field] as string}>{form[field]} (inactivo)</option>
          )}
        </StyledSelect>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <SectionHeader
        title="Equipamiento"
        subtitle="Configuración técnica del carro"
        icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="3" /></svg>}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Motor</label>
            <StyledSelect value={form.motor} onChange={(e) => set('motor', e.target.value)}>
              <option value="">Sin definir</option>
              {availableMotors.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
              {/* Preserva un valor histórico aunque ya no esté habilitado en la categoría */}
              {form.motor && !availableMotors.includes(form.motor) && (
                <option value={form.motor}>{form.motor} (inactivo)</option>
              )}
            </StyledSelect>
            <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">Solo los motores habilitados para esta categoría.</p>
          </div>
          {equipmentFields.map(({ field, fieldKey, label }) => renderCatalogSelect(field, fieldKey, label))}
        </div>
      </SectionHeader>

      <SectionHeader
        title="Documentación y seguimiento"
        subtitle="Estado administrativo de la inscripción, no del equipamiento"
        icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" /><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M3 9h18" /></svg>}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {docFields.map(({ field, fieldKey, label }) => renderCatalogSelect(field, fieldKey, label))}
        </div>
      </SectionHeader>

      <SectionHeader
        title="Estado y publicación"
        subtitle="Qué tan visible es esta ficha hacia afuera"
        icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>}
      >
        <div className="flex flex-col gap-2.5">
          {([
            ['publicacion', 'Publicar ficha', 'Visible en la lista pública de equipos del evento'],
            ['circular', 'Incluir en circular', 'Aparece en la circular oficial enviada a instituciones'],
            ['plataforma', 'Mostrar en plataforma', 'Visible para otros equipos en la plataforma CCCD'],
          ] as const).map(([key, title, subtitle]) => (
            <div key={key} className="flex items-center justify-between rounded-[10px] border border-gray-100 px-3.5 py-3 dark:border-gray-800">
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{title}</p>
                <p className="mt-px text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>
              </div>
              <Toggle checked={form[key]} onChange={(v) => set(key, v)} label="" />
            </div>
          ))}
        </div>
      </SectionHeader>

      <SectionHeader
        title="Ubicación"
        subtitle="De dónde llega el equipo"
        icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s7-6.5 7-11a7 7 0 1 0-14 0c0 4.5 7 11 7 11Z" /><circle cx="12" cy="10" r="2.5" /></svg>}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={labelCls}>País</label>
            <StyledSelect
              value={countryCode}
              onChange={(e) => {
                const country = COUNTRIES.find((c) => c.value === e.target.value);
                set('pais', country?.label ?? '');
                set('municipioDepartamento', '');
              }}
            >
              <option value="">Sin definir</option>
              {COUNTRIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </StyledSelect>
          </div>
          <div>
            <label className={labelCls}>Municipio/Departamento</label>
            {departments.length > 0 ? (
              <StyledSelect value={form.municipioDepartamento} onChange={(e) => set('municipioDepartamento', e.target.value)}>
                <option value="">Sin definir</option>
                {departments.map((d) => (
                  <option key={d.isoCode} value={d.value}>{d.label}</option>
                ))}
              </StyledSelect>
            ) : (
              <input
                className={inputCls}
                value={form.municipioDepartamento}
                onChange={(e) => set('municipioDepartamento', e.target.value)}
                disabled={!countryCode}
                placeholder={countryCode ? '' : 'Selecciona primero el país'}
              />
            )}
          </div>
        </div>
      </SectionHeader>
      </div>

      {error && <Alert variant="error" title="No se pudo guardar la ficha" message={error} />}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={() => setForm(toForm(team))} className={btnSecondary}>
          Cancelar
        </button>
        <button type="button" onClick={handleSave} disabled={saving} className={btnSave}>
          {saving ? 'Guardando...' : 'Guardar ficha'}
        </button>
      </div>
    </div>
  );
}
